import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Ticket, Plus, Trash2, Loader2, Power, CreditCard, Save } from "lucide-react";

const PLAN_LABELS = {
  monthly: "الشهري (شهر)",
  quarterly: "ربع السنوي (3 أشهر)",
  semiannual: "نصف السنوي (6 أشهر)",
  annual: "السنوي (12 شهرًا)",
};

function PlanPricesCard() {
  const [prices, setPrices] = useState(null);
  const [defaults, setDefaults] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/plans").then((r) => {
      const map = {};
      (r.data?.plans || []).forEach((p) => { map[p.id] = p.price; });
      setPrices(map);
      setDefaults(r.data?.defaults || {});
    }).catch(() => toast.error("تعذر تحميل أسعار الخطط"));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/plans", {
        monthly: Number(prices.monthly),
        quarterly: Number(prices.quarterly),
        semiannual: Number(prices.semiannual),
        annual: Number(prices.annual),
      });
      toast.success("تم حفظ أسعار الخطط — ستظهر فورًا لجميع المستخدمين");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "تعذر حفظ الأسعار");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-[#1A1C19]/10 rounded-lg p-5 shadow-3d-sm" data-testid="plan-prices-card">
      <h2 className="font-readex font-semibold text-lg text-[#1A1C19] mb-1 flex items-center gap-2">
        <CreditCard className="w-5 h-5 text-[#6A8A76]" />
        أسعار خطط الاشتراك
      </h2>
      <p className="text-xs text-[#8A8D86] mb-4">عدّل السعر (بالريال SAR) لأي خطة — يُطبق فورًا على صفحة الاشتراك وعمليات الدفع.</p>
      {!prices ? (
        <div className="py-6 flex justify-center text-[#5C605A]"><Loader2 className="w-5 h-5 animate-spin" /></div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {["monthly", "quarterly", "semiannual", "annual"].map((pid) => (
              <label key={pid} className="block">
                <span className="block text-xs font-medium text-[#5C605A] mb-1.5 font-readex">{PLAN_LABELS[pid]}</span>
                <input
                  type="number" min="1" step="0.01"
                  value={prices[pid]}
                  onChange={(e) => setPrices({ ...prices, [pid]: e.target.value })}
                  className="cinput font-mono-num"
                  data-testid={`plan-price-${pid}`}
                />
                {defaults[pid] !== undefined && Number(prices[pid]) !== defaults[pid] && (
                  <span className="text-[10px] text-[#8A8D86] mt-1 block font-mono-num">الافتراضي: {defaults[pid]}</span>
                )}
              </label>
            ))}
          </div>
          <button
            onClick={save}
            disabled={saving || ["monthly","quarterly","semiannual","annual"].some((k) => !prices[k] || Number(prices[k]) <= 0)}
            className="mt-4 bg-[#1F3A33] text-white hover:bg-[#172A25] disabled:opacity-60 rounded-md px-6 py-2.5 font-readex font-medium inline-flex items-center gap-2"
            data-testid="save-plan-prices-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            حفظ الأسعار
          </button>
        </>
      )}
    </div>
  );
}

export default function AdminCouponsPage() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [form, setForm] = useState({ code: "", type: "percent", value: "", expires_at: "", max_uses: "" });
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/coupons");
      setCoupons(r.data || []);
    } catch {
      toast.error("تعذر تحميل الكوبونات");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post("/admin/coupons", {
        code: form.code.trim(),
        type: form.type,
        value: Number(form.value),
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
        max_uses: form.max_uses ? Number(form.max_uses) : null,
      });
      toast.success("تم إنشاء الكوبون");
      setForm({ code: "", type: "percent", value: "", expires_at: "", max_uses: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "تعذر إنشاء الكوبون");
    } finally {
      setCreating(false);
    }
  };

  const toggle = async (c) => {
    setBusy(c.id);
    try {
      await api.patch(`/admin/coupons/${c.id}`, { active: !c.active });
      load();
    } catch {
      toast.error("تعذر التحديث");
    } finally {
      setBusy(null);
    }
  };

  const remove = async (c) => {
    setBusy(c.id);
    try {
      await api.delete(`/admin/coupons/${c.id}`);
      toast.success("تم حذف الكوبون");
      load();
    } catch {
      toast.error("تعذر الحذف");
    } finally {
      setBusy(null);
    }
  };

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("ar-SA-u-nu-latn") : "بدون انتهاء";

  return (
    <div className="space-y-6 fade-up" data-testid="admin-coupons-page">
      <div>
        <div className="text-xs uppercase tracking-wider text-[#8A8D86] mb-2 font-readex">لوحة الإدارة</div>
        <h1 className="font-readex font-semibold text-3xl sm:text-4xl text-[#1A1C19] leading-tight">الخطط والكوبونات</h1>
        <p className="text-[#5C605A] mt-2">عدّل أسعار خطط الاشتراك وأدر كوبونات الخصم.</p>
      </div>

      {/* Plan prices */}
      <PlanPricesCard />

      {/* Create form */}
      <form onSubmit={create} className="bg-white border border-[#1A1C19]/10 rounded-lg p-5 shadow-3d-sm" data-testid="create-coupon-form">
        <h2 className="font-readex font-semibold text-lg text-[#1A1C19] mb-4 flex items-center gap-2">
          <Plus className="w-5 h-5 text-[#6A8A76]" />
          كوبون جديد
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <Field label="الكود">
            <input required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
              placeholder="WELCOME20" dir="ltr" className="cinput font-mono-num" data-testid="coupon-code-input" />
          </Field>
          <Field label="النوع">
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="cinput" data-testid="coupon-type-select">
              <option value="percent">نسبة مئوية %</option>
              <option value="fixed">مبلغ ثابت (ريال)</option>
            </select>
          </Field>
          <Field label={form.type === "percent" ? "النسبة %" : "المبلغ"}>
            <input required type="number" min="0.01" max={form.type === "percent" ? 100 : undefined} step="0.01"
              value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })}
              className="cinput font-mono-num" data-testid="coupon-value-input" />
          </Field>
          <Field label="تاريخ الانتهاء (اختياري)">
            <input type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
              className="cinput" data-testid="coupon-expiry-input" />
          </Field>
          <Field label="حد الاستخدامات (اختياري)">
            <input type="number" min="1" value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
              placeholder="غير محدود" className="cinput font-mono-num" data-testid="coupon-maxuses-input" />
          </Field>
        </div>
        <button type="submit" disabled={creating}
          className="mt-4 bg-[#1F3A33] text-white hover:bg-[#172A25] disabled:opacity-60 rounded-md px-6 py-2.5 font-readex font-medium inline-flex items-center gap-2"
          data-testid="create-coupon-btn">
          {creating && <Loader2 className="w-4 h-4 animate-spin" />}
          إنشاء الكوبون
        </button>
      </form>

      {/* List */}
      <div className="bg-white border border-[#1A1C19]/10 rounded-lg overflow-hidden shadow-3d-sm">
        {loading ? (
          <div className="p-10 flex justify-center text-[#5C605A]"><Loader2 className="w-5 h-5 animate-spin" /></div>
        ) : coupons.length === 0 ? (
          <div className="p-10 text-center text-[#8A8D86] font-readex">
            <Ticket className="w-8 h-8 mx-auto mb-2" strokeWidth={1.5} />
            لا توجد كوبونات بعد
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm" data-testid="coupons-table">
              <thead>
                <tr className="bg-[#F5F4EF] text-[#5C605A] text-xs uppercase font-readex">
                  <th className="px-4 py-3 text-right">الكود</th>
                  <th className="px-4 py-3 text-center">الخصم</th>
                  <th className="px-4 py-3 text-center">الانتهاء</th>
                  <th className="px-4 py-3 text-center">الاستخدامات</th>
                  <th className="px-4 py-3 text-center">الحالة</th>
                  <th className="px-4 py-3 text-center">إجراءات</th>
                </tr>
              </thead>
              <tbody>
                {coupons.map((c) => (
                  <tr key={c.id} className="border-t border-[#1A1C19]/8" data-testid={`coupon-row-${c.code}`}>
                    <td className="px-4 py-3 text-right font-mono-num font-medium" dir="ltr" style={{ textAlign: "end" }}>{c.code}</td>
                    <td className="px-4 py-3 text-center font-mono-num">{c.type === "percent" ? `${c.value}%` : `${c.value} ر.س`}</td>
                    <td className="px-4 py-3 text-center text-[#5C605A]">{fmtDate(c.expires_at)}</td>
                    <td className="px-4 py-3 text-center font-mono-num">{c.uses || 0}{c.max_uses ? ` / ${c.max_uses}` : ""}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-readex ${c.active ? "bg-[#EAF2ED] text-[#1F3A33]" : "bg-[#F5F4EF] text-[#8A8D86]"}`}>
                        {c.active ? "نشط" : "معطل"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex justify-center gap-1">
                        <button onClick={() => toggle(c)} disabled={busy === c.id} title={c.active ? "تعطيل" : "تفعيل"}
                          className="w-8 h-8 inline-flex items-center justify-center rounded-md text-[#5C605A] hover:bg-[#F0EFEB]"
                          data-testid={`toggle-coupon-${c.code}`}>
                          <Power className="w-4 h-4" />
                        </button>
                        <button onClick={() => remove(c)} disabled={busy === c.id} title="حذف"
                          className="w-8 h-8 inline-flex items-center justify-center rounded-md text-[#C84C31] hover:bg-[#C84C31]/10"
                          data-testid={`delete-coupon-${c.code}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <style>{`
        .cinput { width:100%; background:white; border:1px solid rgba(26,28,25,0.15); border-radius:.375rem; padding:.6rem .85rem; font-size:.9rem; }
        .cinput:focus { outline:none; border-color:#1F3A33; box-shadow:0 0 0 3px rgba(31,58,51,0.12); }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-[#5C605A] mb-1.5 font-readex">{label}</span>
      {children}
    </label>
  );
}
